import { useEffect, useRef, useState } from "react";
import { DataGrid, GridColDef, GridPaginationModel } from "@mui/x-data-grid";
import {
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
} from "@mui/material";
import {
  gridSx,
  LoadingOverlay,
  StatusBadge,
  useNewLeadsContext,
} from "../NewLeads";
import {
  getCompletedLeads,
  getChatCallHistory,
  getCallRecordingStream,
  getCallTranscript,
  CallTranscriptResponse,
  isTranscriptPending,
} from "../../../services/newLeads";
import moment from "moment";

const PAGE_SIZE = 10;

const DetailRow = ({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) => (
  <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
    <span
      style={{
        fontSize: 11,
        fontWeight: 600,
        color: "#9ca3af",
        textTransform: "uppercase",
        letterSpacing: "0.05em",
      }}
    >
      {label}
    </span>
    <span style={{ fontSize: 13, color: "#111827", fontWeight: 500 }}>
      {value || "--"}
    </span>
  </div>
);

const historyIconMap: Record<
  string,
  { icon: string; color: string; bg: string }
> = {
  call: { icon: "bi bi-telephone-fill", color: "#0284c7", bg: "#e0f2fe" },
  message: { icon: "bi bi-chat-dots-fill", color: "#7c3aed", bg: "#ede9fe" },
  default: { icon: "bi bi-check-circle-fill", color: "#16a34a", bg: "#dcfce7" },
};

const callStatusColor: Record<string, string> = {
  completed: "#16a34a",
  "in-progress": "#f59e0b",
  initiated: "#6b7280",
  failed: "#dc2626",
};

const LeadDetailDialog = ({
  lead,
  onClose,
}: {
  lead: any;
  onClose: () => void;
}) => {
  const patientName =
    `${lead?.firstName || ""} ${lead?.lastName || ""}`.trim() || "--";
  const history: any[] = lead?.data || [];

  const [calls, setCalls] = useState<any[]>([]);
  const [messages, setMessages] = useState<any[]>([]);
  const [callHistoryLoading, setCallHistoryLoading] = useState(false);
  const [playingCallId, setPlayingCallId] = useState<number | null>(null);
  const [loadingCallId, setLoadingCallId] = useState<number | null>(null);
  const [transcripts, setTranscripts] = useState<Record<number, CallTranscriptResponse>>({});
  const [transcriptLoadingId, setTranscriptLoadingId] = useState<number | null>(null);
  const [openTranscriptId, setOpenTranscriptId] = useState<number | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const prevLeadId = useRef<any>(null);

  useEffect(() => {
    if (!lead) {
      setCalls([]);
      setMessages([]);
      prevLeadId.current = null;
      audioRef.current?.pause();
      audioRef.current = null;
      setPlayingCallId(null);
      setTranscripts({});
      setTranscriptLoadingId(null);
      setOpenTranscriptId(null);
      return;
    }
    const currentId = lead.id ?? lead.requestId ?? lead.leadId;
    if (prevLeadId.current === currentId) return;
    prevLeadId.current = currentId;
    setCallHistoryLoading(true);
    getChatCallHistory({
      clinicId: lead.clinicId,
      conciergeLeadId: lead.conciergeLead,
      conversationId: lead.conversationId,
    })
      .then((res: any) => {
        if (res && res.error === false) {
          const data = res.data || {};
          setCalls(
            [...(data.calls || [])].sort(
              (a: any, b: any) =>
                new Date(a.createdAt).getTime() -
                new Date(b.createdAt).getTime()
            )
          );
          setMessages(
            [...(data.messages || [])].sort(
              (a: any, b: any) =>
                new Date(a.dateCreated).getTime() -
                new Date(b.dateCreated).getTime()
            )
          );
        }
      })
      .catch(() => {})
      .finally(() => setCallHistoryLoading(false));
  }, [lead]);

  const handleShowTranscript = async (call: any) => {
    if (openTranscriptId === call.id) {
      setOpenTranscriptId(null);
      return;
    }
    setOpenTranscriptId(call.id);
    setTranscriptLoadingId(call.id);
    try {
      const data = await getCallTranscript({ callId: call.id, clinicId: call.clinicId });
      setTranscripts((prev) => ({ ...prev, [call.id]: data }));
    } catch {
      setTranscripts((prev) => ({ ...prev, [call.id]: null }));
    } finally {
      setTranscriptLoadingId(null);
    }
  };

  const handlePlayCall = async (call: any) => {
    if (playingCallId === call.id) {
      audioRef.current?.pause();
      setPlayingCallId(null);
      return;
    }
    setLoadingCallId(call.id);
    try {
      const res = await getCallRecordingStream({
        callId: call.id,
        clinicId: call.clinicId,
      });
      const blobUrl = URL.createObjectURL(res.data);
      audioRef.current?.pause();
      const audio = new Audio(blobUrl);
      audioRef.current = audio;
      setPlayingCallId(call.id);
      audio.play();
      audio.onended = () => setPlayingCallId(null);
    } catch {
      // ignore
    } finally {
      setLoadingCallId(null);
    }
  };

  return (
    <Dialog
      open={!!lead}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ style: { borderRadius: 10 } }}
    >
      <DialogTitle
        sx={{
          backgroundColor: "#194e5b",
          color: "white",
          borderTopLeftRadius: 10,
          borderTopRightRadius: 10,
          fontSize: 16,
          fontWeight: 600,
          py: 1.5,
          px: 2.5,
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          position: "relative",
        }}
      >
        <div
          style={{
            width: 36,
            height: 36,
            borderRadius: "50%",
            background: "rgba(255,255,255,0.2)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 16,
            fontWeight: 700,
            flexShrink: 0,
          }}
        >
          {patientName.charAt(0).toUpperCase()}
        </div>
        <div>
          <div style={{ fontSize: 15, fontWeight: 700 }}>{patientName}</div>
          <div style={{ fontSize: 11, opacity: 0.75, fontWeight: 400 }}>
            {moment(lead?.createdAt).format("MMM D, YYYY • h:mm a")}
          </div>
        </div>
        <span
          style={{
            marginLeft: "auto",
            background: "#16a34a",
            color: "#fff",
            borderRadius: 6,
            padding: "3px 10px",
            fontSize: 11,
            fontWeight: 600,
            marginRight: 36,
          }}
        >
          {lead?.subStatus || lead?.status}
        </span>
        <IconButton
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: "50%",
            transform: "translateY(-50%)",
            color: "white",
            "&:hover": { backgroundColor: "rgba(255,255,255,0.15)" },
          }}
        >
          <i className="bi bi-x-lg" style={{ fontSize: 14 }} />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 2.5 }}>
        {/* Patient Info */}
        <div
          style={{
            background: "#f9fafb",
            borderRadius: 10,
            padding: 14,
            marginBottom: 12,
            marginTop: 4,
          }}
        >
          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              color: "#6b7280",
              marginBottom: 12,
              textTransform: "uppercase",
              letterSpacing: "0.06em",
            }}
          >
            <i className="bi bi-person" style={{ marginRight: 6 }} />
            Patient Information
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "12px 24px",
            }}
          >
            <DetailRow label="Full Name" value={patientName} />
            <DetailRow label="Phone" value={lead?.phone} />
            <DetailRow label="Email" value={lead?.email} />
            <DetailRow label="Insurance" value={lead?.insuranceName} />
            <DetailRow label="City" value={lead?.city} />
            <DetailRow label="Postal Code" value={lead?.zipcode} />
            {lead?.formattedAddress && (
              <div style={{ gridColumn: "1 / -1" }}>
                <DetailRow label="Address" value={lead.formattedAddress} />
              </div>
            )}
          </div>
        </div>

        {/* Clinic & Request */}
        <div
          style={{
            background: "#f9fafb",
            borderRadius: 10,
            padding: 14,
            marginBottom: 12,
          }}
        >
          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              color: "#6b7280",
              marginBottom: 12,
              textTransform: "uppercase",
              letterSpacing: "0.06em",
            }}
          >
            <i className="bi bi-hospital" style={{ marginRight: 6 }} />
            Clinic & Request
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "12px 24px",
            }}
          >
            <DetailRow label="Clinic" value={lead?.clinicName} />
            <DetailRow label="Sub Status" value={lead?.subStatus} />
            {lead?.clinicAddress && (
              <div style={{ gridColumn: "1 / -1" }}>
                <DetailRow label="Clinic Address" value={lead.clinicAddress} />
              </div>
            )}
          </div>
        </div>

        {/* Activity Timeline */}
        {history.length > 0 && (
          <div
            style={{
              background: "#f9fafb",
              borderRadius: 10,
              padding: 14,
              marginBottom: 12,
            }}
          >
            <div
              style={{
                fontSize: 11,
                fontWeight: 700,
                color: "#6b7280",
                marginBottom: 14,
                textTransform: "uppercase",
                letterSpacing: "0.06em",
              }}
            >
              <i className="bi bi-clock-history" style={{ marginRight: 6 }} />
              Activity Timeline ({history.length})
            </div>
            <div style={{ display: "flex", flexDirection: "column" }}>
              {history.map((h: any, i: number) => {
                const meta = historyIconMap[h.type] || historyIconMap.default;
                return (
                  <div key={i} style={{ display: "flex", gap: 12 }}>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        flexShrink: 0,
                      }}
                    >
                      <div
                        style={{
                          width: 28,
                          height: 28,
                          borderRadius: "50%",
                          background: meta.bg,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <i
                          className={meta.icon}
                          style={{ fontSize: 12, color: meta.color }}
                        />
                      </div>
                      {i < history.length - 1 && (
                        <div
                          style={{
                            width: 2,
                            flex: 1,
                            background: "#e5e7eb",
                            margin: "4px 0",
                            minHeight: 12,
                          }}
                        />
                      )}
                    </div>
                    <div
                      style={{
                        paddingBottom: i < history.length - 1 ? 14 : 0,
                        flex: 1,
                        minWidth: 0,
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                          flexWrap: "wrap",
                        }}
                      >
                        <span
                          style={{
                            fontSize: 13,
                            fontWeight: 600,
                            color: "#111827",
                          }}
                        >
                          {h.title || "--"}
                        </span>
                        {h.status && (
                          <span
                            style={{
                              fontSize: 11,
                              fontWeight: 600,
                              color: callStatusColor[h.status] || "#6b7280",
                              background:
                                h.status === "completed"
                                  ? "#dcfce7"
                                  : h.status === "in-progress"
                                  ? "#fef3c7"
                                  : "#f3f4f6",
                              borderRadius: 6,
                              padding: "1px 7px",
                            }}
                          >
                            {h.status}
                          </span>
                        )}
                      </div>
                      {h.description && (
                        <div
                          style={{
                            fontSize: 12,
                            color: "#6b7280",
                            marginTop: 2,
                          }}
                        >
                          {h.description}
                        </div>
                      )}
                      {h.notes && (
                        <div
                          style={{
                            fontSize: 12,
                            color: "#374151",
                            marginTop: 3,
                            fontStyle: "italic",
                          }}
                        >
                          "{h.notes}"
                        </div>
                      )}
                      <div
                        style={{
                          fontSize: 11,
                          color: "#9ca3af",
                          marginTop: 3,
                        }}
                      >
                        {moment(h.date).format("MMM D, YYYY • h:mm a")}
                        {h.callCompletedAt && (
                          <span style={{ marginLeft: 8 }}>
                            → Ended {moment(h.callCompletedAt).format("h:mm a")}
                          </span>
                        )}
                        {h.duration !== undefined &&
                          h.duration !== null &&
                          h.duration !== "" && (
                            <span style={{ marginLeft: 8 }}>
                              · {h.duration}
                            </span>
                          )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Calls */}
        <div
          style={{
            background: "#f9fafb",
            borderRadius: 10,
            padding: 14,
            marginBottom: 12,
          }}
        >
          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              color: "#6b7280",
              marginBottom: 12,
              textTransform: "uppercase",
              letterSpacing: "0.06em",
            }}
          >
            <i className="bi bi-telephone" style={{ marginRight: 6 }} />
            Calls ({calls.length})
          </div>
          {callHistoryLoading ? (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                padding: "12px 0",
              }}
            >
              <CircularProgress size={20} sx={{ color: "#194e5b" }} />
            </div>
          ) : calls.length === 0 ? (
            <div
              style={{
                fontSize: 12,
                color: "#9ca3af",
                textAlign: "center",
                padding: "8px 0",
              }}
            >
              No calls available.
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {calls.map((call: any) => {
                const isPlaying = playingCallId === call.id;
                const isLoadingAudio = loadingCallId === call.id;
                const duration =
                  call.metadata?.recordingDuration ?? call.metadata?.duration;
                const hasRecording = !!call.recordingUrl;
                return (
                  <div
                    key={call.id}
                    style={{ background: "#fff", borderRadius: 8, border: "1px solid #e5e7eb", overflow: "hidden" }}
                  >
                    {/* Row */}
                    <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 12px" }}>
                      <div style={{ width: 28, height: 28, borderRadius: "50%", background: "#e0f2fe", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                        <i
                          className={call.isIncoming ? "bi bi-telephone-inbound-fill" : "bi bi-telephone-outbound-fill"}
                          style={{ fontSize: 11, color: "#0284c7" }}
                        />
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                          <span style={{ fontSize: 13, fontWeight: 600, color: "#111827" }}>
                            {call.isIncoming ? "Incoming" : "Outgoing"}
                          </span>
                          {duration && (
                            <span style={{ fontSize: 11, color: "#6b7280" }}>· {duration}s</span>
                          )}
                        </div>
                        <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 2 }}>
                          {moment(call.createdAt).format("MMM D, YYYY • h:mm a")}
                        </div>
                      </div>
                      {/* Transcript button */}
                      <button
                        onClick={() => handleShowTranscript(call)}
                        title="View transcript"
                        style={{
                          width: 32, height: 32, borderRadius: "50%", border: "none", flexShrink: 0,
                          background: openTranscriptId === call.id ? "#ede9fe" : "#f3f4f6",
                          color: openTranscriptId === call.id ? "#7c3aed" : "#6b7280",
                          cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
                        }}
                      >
                        {transcriptLoadingId === call.id
                          ? <CircularProgress size={12} sx={{ color: "#7c3aed" }} />
                          : <i className="bi bi-file-text" style={{ fontSize: 13 }} />
                        }
                      </button>
                      {/* Play button */}
                      <button
                        onClick={() => handlePlayCall(call)}
                        disabled={!hasRecording || isLoadingAudio}
                        title={hasRecording ? (isPlaying ? "Pause" : "Play recording") : "No recording available"}
                        style={{
                          width: 32, height: 32, borderRadius: "50%", border: "none", flexShrink: 0,
                          background: !hasRecording ? "#f3f4f6" : isPlaying ? "#fee2e2" : "#e0f2fe",
                          color: !hasRecording ? "#d1d5db" : isPlaying ? "#dc2626" : "#0284c7",
                          cursor: hasRecording && !isLoadingAudio ? "pointer" : "not-allowed",
                          display: "flex", alignItems: "center", justifyContent: "center",
                        }}
                      >
                        {isLoadingAudio
                          ? <i className="bi bi-hourglass-split" style={{ fontSize: 11 }} />
                          : isPlaying
                          ? <i className="bi bi-pause-fill" style={{ fontSize: 14 }} />
                          : <i className="bi bi-play-fill" style={{ fontSize: 14 }} />
                        }
                      </button>
                    </div>
                    {/* Transcript panel */}
                    {openTranscriptId === call.id && (
                      <div style={{ borderTop: "1px solid #f3f4f6", padding: "10px 12px", background: "#fafafa", maxHeight: 220, overflowY: "auto" }}>
                        {transcriptLoadingId === call.id ? (
                          <div style={{ display: "flex", justifyContent: "center", padding: "4px 0" }}>
                            <CircularProgress size={16} sx={{ color: "#7c3aed" }} />
                          </div>
                        ) : transcripts[call.id]?.transcript?.length ? (
                          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                            {transcripts[call.id]!.transcript!.map((seg, i) => {
                              const isPatient = seg.speaker === "Patient";
                              const start = parseFloat(seg.startTime);
                              const m = Math.floor(start / 60);
                              const s = Math.floor(start % 60);
                              const timeLabel = m > 0 ? `${m}:${s.toString().padStart(2, "0")}` : `${s}s`;
                              return (
                                <div key={i} style={{ display: "flex", flexDirection: "column", alignItems: isPatient ? "flex-start" : "flex-end" }}>
                                  <span style={{ fontSize: 10, color: "#9ca3af", marginBottom: 2, paddingLeft: isPatient ? 2 : 0, paddingRight: isPatient ? 0 : 2 }}>
                                    {seg.speaker} · {timeLabel}
                                  </span>
                                  <div style={{
                                    maxWidth: "80%",
                                    background: isPatient ? "#fff" : "#194e5b",
                                    color: isPatient ? "#111827" : "#fff",
                                    border: isPatient ? "1px solid #e5e7eb" : "none",
                                    borderRadius: isPatient ? "0 8px 8px 8px" : "8px 0 8px 8px",
                                    padding: "5px 10px",
                                    fontSize: 12,
                                    lineHeight: 1.5,
                                  }}>
                                    {seg.text}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        ) : isTranscriptPending(transcripts[call.id]) ? (
                          <p style={{ margin: 0, fontSize: 12, color: "#9ca3af", textAlign: "center" }}>
                            Transcript will be available shortly
                          </p>
                        ) : (
                          <p style={{ margin: 0, fontSize: 12, color: "#9ca3af", textAlign: "center" }}>
                            No transcript available.
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Messages */}
        <div style={{ background: "#f9fafb", borderRadius: 10, padding: 14 }}>
          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              color: "#6b7280",
              marginBottom: 12,
              textTransform: "uppercase",
              letterSpacing: "0.06em",
            }}
          >
            <i className="bi bi-chat-dots" style={{ marginRight: 6 }} />
            Messages ({messages.length})
          </div>
          {callHistoryLoading ? (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                padding: "12px 0",
              }}
            >
              <CircularProgress size={20} sx={{ color: "#194e5b" }} />
            </div>
          ) : messages.length === 0 ? (
            <div
              style={{
                fontSize: 12,
                color: "#9ca3af",
                textAlign: "center",
                padding: "8px 0",
              }}
            >
              No messages available.
            </div>
          ) : (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 6,
                maxHeight: 260,
                overflowY: "auto",
              }}
            >
              {messages.map((msg: any) => {
                const isPatient = msg.sentByPatient;
                return (
                  <div
                    key={msg.sid}
                    style={{
                      display: "flex",
                      justifyContent: isPatient ? "flex-start" : "flex-end",
                    }}
                  >
                    <div
                      style={{
                        maxWidth: "75%",
                        background: isPatient ? "#fff" : "#194e5b",
                        color: isPatient ? "#111827" : "#fff",
                        border: isPatient ? "1px solid #e5e7eb" : "none",
                        borderRadius: isPatient
                          ? "0 10px 10px 10px"
                          : "10px 0 10px 10px",
                        padding: "8px 12px",
                      }}
                    >
                      <div style={{ fontSize: 13, lineHeight: 1.4 }}>
                        {msg.body}
                      </div>
                      <div
                        style={{
                          fontSize: 10,
                          marginTop: 4,
                          opacity: 0.6,
                          textAlign: isPatient ? "left" : "right",
                        }}
                      >
                        {moment(msg.dateCreated).format("MMM D, YYYY • h:mm a")}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const Completed = () => {
  const { selectedClinicIds, startDate, endDate } = useNewLeadsContext();

  const [rows, setRows] = useState<any[]>([]);
  const [totalRows, setTotalRows] = useState(0);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: PAGE_SIZE,
  });
  const [selectedLead, setSelectedLead] = useState<any>(null);

  const primaryClinicId = selectedClinicIds[0];

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPaginationModel((prev) => ({ ...prev, page: 0 }));
    }, 500);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    setLoading(true);
    getCompletedLeads({
      page: paginationModel.page + 1,
      record: paginationModel.pageSize,
      clinicId: primaryClinicId || undefined,
      startDate: startDate || undefined,
      endDate: endDate || undefined,
      search: debouncedSearch || undefined,
    })
      .then((res) => {
        if (res && res.status && !res.status.error) {
          const leads = res.leads || [];
          const mapped = leads.map((l: any, i: number) => ({
            ...l,
            id: l.id ?? l.requestId ?? l.leadId ?? i,
            patientName: `${l.firstName || ""} ${l.lastName || ""}`.trim(),
          }));
          setRows(mapped);
          setTotalRows(res.total ?? leads.length ?? 0);
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [
    paginationModel.page,
    paginationModel.pageSize,
    primaryClinicId,
    debouncedSearch,
    startDate,
    endDate,
  ]);

  const columns: GridColDef[] = [
    {
      field: "clinicName",
      headerName: "Clinic",
      minWidth: 240,
    },
    {
      field: "patientName",
      headerName: "Patient",
      minWidth: 250,
      renderCell: (params) => (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            lineHeight: 1.2,
            height: "100%",
          }}
        >
          <div style={{ fontWeight: 600, fontSize: 14, color: "#111827" }}>
            {params.row.patientName}
          </div>
          <div style={{ color: "#9ca3af", fontSize: 10 }}>
            {params.row.phone}
          </div>
          <div style={{ color: "#9ca3af", fontSize: 10 }}>
            {params.row.email}
          </div>
        </div>
      ),
    },
    {
      field: "status",
      headerName: "Status",
      minWidth: 160,
      renderCell: (params) => <StatusBadge status={params.row.status} />,
    },
    {
      field: "subStatus",
      headerName: "Sub Status",
      minWidth: 160,
      renderCell: (params) => (
        <span
          style={{
            background: "#dcfce7",
            color: "#16a34a",
            borderRadius: 8,
            padding: "3px 10px",
            fontSize: 12,
            fontWeight: 600,
          }}
        >
          {params.row.subStatus || params.row.status || "--"}
        </span>
      ),
    },
    {
      field: "createdAt",
      headerName: "Time",
      minWidth: 200,
      renderCell: (params) => (
        <span style={{ color: "#374151", fontSize: 13 }}>
          {params.row.createdAt
            ? moment(params.row.createdAt).format("MMM D, YYYY • h:mm a")
            : "--"}
        </span>
      ),
    },
    {
      field: "activity",
      headerName: "Activity",
      minWidth: 110,
      renderCell: (params) => {
        const count = params.row.data?.length ?? 0;
        return (
          <span
            style={{
              background: "#f3f4f6",
              borderRadius: 8,
              padding: "3px 10px",
              fontSize: 12,
              fontWeight: 600,
              color: "#374151",
            }}
          >
            {count} event{count !== 1 ? "s" : ""}
          </span>
        );
      },
    },
    {
      field: "actions",
      headerName: "",
      width: 128,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <button
          onClick={(e) => {
            e.stopPropagation();
            setSelectedLead(params.row);
          }}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 5,
            border: "1px solid #d1d5db",
            borderRadius: 7,
            background: "#fff",
            cursor: "pointer",
            padding: "5px 12px",
            fontSize: 12,
            fontWeight: 600,
            color: "#374151",
            whiteSpace: "nowrap",
            height: 30,
          }}
          onMouseEnter={(e) => {
            (e.currentTarget as HTMLButtonElement).style.background = "#f3f4f6";
            (e.currentTarget as HTMLButtonElement).style.borderColor =
              "#9ca3af";
          }}
          onMouseLeave={(e) => {
            (e.currentTarget as HTMLButtonElement).style.background = "#fff";
            (e.currentTarget as HTMLButtonElement).style.borderColor =
              "#d1d5db";
          }}
        >
          View Details
        </button>
      ),
    },
  ];

  return (
    <>
      <div
        style={{
          background: "#fff",
          borderRadius: 12,
          border: "1px solid #e5e7eb",
          overflow: "hidden",
          margin: 20,
          position: "relative",
        }}
      >
        {loading && <LoadingOverlay />}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "16px 20px",
            borderBottom: "1px solid #f3f4f6",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ fontWeight: 700, fontSize: 17, color: "#111827" }}>
              Completed Requests
            </span>
            <span
              style={{
                background: "#f3f4f6",
                borderRadius: 12,
                padding: "2px 10px",
                fontSize: 13,
                fontWeight: 600,
                color: "#374151",
              }}
            >
              {totalRows} total
            </span>
          </div>

          <div style={{ position: "relative" }}>
            <i
              className="bi bi-search"
              style={{
                position: "absolute",
                left: 12,
                top: "50%",
                transform: "translateY(-50%)",
                color: "#9ca3af",
                fontSize: 14,
                pointerEvents: "none",
              }}
            />
            <input
              type="text"
              placeholder="Search leads..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{
                border: "1px solid #d1d5db",
                borderRadius: 8,
                padding: "9px 14px 9px 36px",
                fontSize: 14,
                color: "#374151",
                background: "#fff",
                width: 280,
                outline: "none",
              }}
            />
          </div>
        </div>

        <DataGrid
          rows={rows}
          columns={columns}
          getRowId={(row) => row.id ?? row.requestId ?? row.leadId}
          disableColumnMenu
          paginationMode="server"
          rowCount={totalRows}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[10, 25, 50]}
          density="standard"
          sx={gridSx}
        />
      </div>

      <LeadDetailDialog
        lead={selectedLead}
        onClose={() => setSelectedLead(null)}
      />
    </>
  );
};
